import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  EntityScore,
  ScorecardReport,
} from '@internal/plugin-scorecard-common';

export interface ScorecardApi {
  report(refresh?: boolean): Promise<ScorecardReport>;
  forEntity(entityRef: string): Promise<EntityScore>;
}

export const scorecardApiRef = createApiRef<ScorecardApi>({
  id: 'plugin.scorecard.service',
});

export class ScorecardClient implements ScorecardApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async request<T>(path: string): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('scorecard');
    const res = await this.fetchApi.fetch(`${base}${path}`);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  async report(refresh = false) {
    return this.request<ScorecardReport>(
      `/report${refresh ? '?refresh=true' : ''}`,
    );
  }

  async forEntity(entityRef: string) {
    return this.request<EntityScore>(
      `/entities/${encodeURIComponent(entityRef)}`,
    );
  }
}
